import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import '../styles/Employees.css';
import moment from 'moment';

// Import booking level images
import underbooked from '../assets/images/underbooked.png';
import booked from '../assets/images/booked.png';
import overbooked from '../assets/images/overbooked.png';

const Employees = () => {
    const [employees, setEmployees] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [activeTab, setActiveTab] = useState('employees');
    const [taskSearchTerm, setTaskSearchTerm] = useState('');
    const navigate = useNavigate();

    // Add dark theme by default
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', 'dark');
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [employeesRes, schedulesRes, companyRes] = await Promise.all([
                    api.get('/api/employees'),
                    api.get('/api/schedules'),
                    api.get('/api/companies/info')
                ]);

                // Create a mapping between user_ids and employee IDs
                const userIdToEmployeeId = {};
                employeesRes.data.forEach(employee => {
                    if (employee.user_id) {
                        userIdToEmployeeId[employee.user_id] = employee.id;
                    }
                });

                // Convert schedules to calendar format
                const formattedSchedules = schedulesRes.data.map(schedule => {
                    const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                    const shiftDate = schedule.shift_date.split('T')[0];
                    const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                    const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                    
                    return {
                        id: schedule.id,
                        calendarId: schedule.calendar_type || 'tasks',
                        title: schedule.title || 'Untitled Schedule',
                        start: startTime.toDate(),
                        end: endTime.toDate(),
                        resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined
                    };
                });

                setEmployees(employeesRes.data);
                setSchedules(formattedSchedules);
                setCompanyInfo(companyRes.data);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch data');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const calculateMonthlyHours = (employeeId) => {
        const currentMonth = moment().month();
        const currentYear = moment().year();
        
        const employeeSchedules = schedules.filter(schedule => {
            const scheduleDate = moment(schedule.start);
            return schedule.resource === String(employeeId) &&
                   scheduleDate.month() === currentMonth &&
                   scheduleDate.year() === currentYear;
        });

        const totalHours = employeeSchedules.reduce((total, schedule) => {
            // Only count hours for:
            // 1. Regular work schedules (no countsTowardHours property)
            // 2. PTO requests (countsTowardHours === true)
            // Skip regular time-off requests (countsTowardHours === false)
            if (schedule.hasOwnProperty('countsTowardHours') && !schedule.countsTowardHours) {
                return total; // Skip this event - it's regular time-off, not PTO
            }
            
            const start = moment(schedule.start);
            const end = moment(schedule.end);
            const duration = moment.duration(end.diff(start));
            return total + duration.asHours();
        }, 0);

        return Math.round(totalHours);
    };

    const getRandomPastelColor = (seed) => {
        const colors = [
            '#2196F3', // blue
            '#9C27B0', // purple
            '#4CAF50', // green
            '#FF9800', // orange
            '#E91E63', // pink
            '#00BCD4', // cyan
            '#009688', // teal
            '#607D8B'  // blue gray
        ];
        const index = Math.abs(seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colors.length;
        return colors[index];
    };

    const getBookingImage = (hours) => {
        const bookingPercentage = (hours / 160) * 100;
        
        if (bookingPercentage < 33) {
            return underbooked;
        } else if (bookingPercentage < 66) {
            return booked;
        } else {
            return overbooked;
        }
    };

    const renderAvatar = (employee) => {
        const initial = employee.first_name?.charAt(0)?.toUpperCase() || 
                       employee.email?.charAt(0)?.toUpperCase();
        const backgroundColor = getRandomPastelColor(employee.email);
        
        return (
            <div 
                className="employee-avatar" 
                style={{ backgroundColor }}
            >
                {initial}
            </div>
        );
    };

    if (loading) {
        return <div className="employees-page loading">Loading...</div>;
    }

    if (error) {
        return <div className="employees-page error">Error: {error}</div>;
    }

    const handleNavigation = (route) => {
        navigate(route);
    };

    // Add function to handle view toggle
    const handleViewToggle = () => {
        // Get the token from localStorage
        const token = localStorage.getItem('token') || localStorage.getItem('employeeToken');
        
        if (token) {
            // Store the token in both localStorage and sessionStorage for the employee app
            localStorage.setItem('employeeToken', token);
            sessionStorage.setItem('employeeToken', token);
            
            // Navigate to the employee calendar
            window.location.href = 'https://calendar.joinoptivo.com';
        } else {
            console.error('No token found for navigation');
        }
    };

    const handleTabClick = (tab, event) => {
        if (event) {
            event.preventDefault();
        }
        setActiveTab(tab);
        if (tab === 'dashboard') {
            navigate('/dashboard');
        }
    };

    // Filter employees based on search term
    const filteredEmployees = employees.filter(employee => {
        if (!taskSearchTerm) return true;
        const searchLower = taskSearchTerm.toLowerCase();
        return (
            employee.first_name?.toLowerCase().includes(searchLower) ||
            employee.last_name?.toLowerCase().includes(searchLower) ||
            employee.email?.toLowerCase().includes(searchLower) ||
            employee.role?.toLowerCase().includes(searchLower)
        );
    });

    return (
        <div className="dashboard">
            <div className="top-header" style={{
                left: '0',  // Override the CSS that sets left: 300px
                paddingLeft: '24px'  // Add padding to match the original design
            }}>
                <h1 style={{ 
                    color: 'white', 
                    fontFamily: 'Albert Sans, sans-serif',
                    fontWeight: '700',
                    fontSize: '48px',
                    margin: '0',
                    position: 'absolute',
                    left: '24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 100
                }}>{companyInfo?.name || 'Loading...'}</h1>
                
                <div style={{ 
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '24px'
                }}>
                    {/* View Toggle Switch replaces navigation */}
                    <div style={{
                        width: '200px',
                        height: '44px',
                        backgroundColor: '#2D2D2D',
                        borderRadius: '22px',
                        padding: '4px',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative'
                    }} onClick={handleViewToggle}>
                        <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: 'calc(50% - 4px)',
                            height: '36px',
                            backgroundColor: '#1E1E1E',
                            borderRadius: '18px',
                            transition: 'all 0.3s ease',
                            zIndex: 1
                        }}></div>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            height: '36px',
                            width: '100%',
                            zIndex: 2
                        }}>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Calendar</div>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Manager</div>
                        </div>
                    </div>
                </div>

                {/* Task Search Box - Far Right Corner */}
                <div className="task-search-box" style={{ 
                    position: 'absolute',
                    right: '24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '250px',
                    display: 'flex',
                    alignItems: 'center',
                    background: 'var(--bg-secondary)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    zIndex: 2
                }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                        <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input 
                        type="text" 
                        placeholder="Search Tasks" 
                        value={taskSearchTerm}
                        onChange={(e) => setTaskSearchTerm(e.target.value)}
                        style={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif'
                        }}
                    />
                </div>
            </div>

            <div className="company-info" style={{
                left: '0',  // Override the CSS that sets left: 300px
                paddingLeft: '24px'  // Add padding to match the original design
            }}>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', position: 'relative' }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        width: '400px',
                        marginLeft: '24px'
                    }}>
                        <div className="sub-nav-menu" style={{ marginLeft: '16px' }}>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('dashboard', event)}
                            >
                                Dashboard
                            </a>
                            <a 
                                href="#" 
                                className={`sub-nav-item ${activeTab === 'employees' ? 'active' : ''}`}
                                onClick={(event) => handleTabClick('employees', event)}
                            >
                                Employees
                            </a>
                        </div>
                    </div>

                    <div className="search-box" style={{ 
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        background: 'var(--bg-secondary)',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        border: '1px solid var(--border-color)',
                        zIndex: 1
                    }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                            <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input 
                            type="text" 
                            placeholder="Team Member Search" 
                            style={{
                                border: 'none',
                                outline: 'none',
                                width: '100%',
                                background: 'transparent',
                                color: 'var(--text-primary)',
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif'
                            }}
                        />
                    </div>
                    
                    <div className="people-avatars" style={{ marginLeft: 'auto', width: '400px', display: 'flex', justifyContent: 'flex-end' }}>
                        {employees && employees.length > 0 ? employees
                            .filter(employee => employee.role === 'manager')
                            .map(employee => {
                                const initial = employee.first_name?.charAt(0)?.toUpperCase() || 
                                               employee.email?.charAt(0)?.toUpperCase();
                                const backgroundColor = getRandomPastelColor(employee.email);
                                
                                return (
                                    <div 
                                        key={employee.id}
                                        className="avatar" 
                                        title={employee.email}
                                        style={{ backgroundColor }}
                                    >
                                        {initial}
                                    </div>
                                );
                            }) : null}
                        <div 
                            className="avatar add-manager" 
                            style={{ 
                                border: `2px dashed var(--border-color)`,
                                backgroundColor: 'transparent',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer'
                            }}
                            title="Add Manager"
                        >
                            +
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="employees-content">
                <div className="employees-grid">
                    {filteredEmployees.map(employee => {
                        const monthlyHours = calculateMonthlyHours(employee.id);
                        return (
                            <div key={employee.id} className="employee-card">
                                <div className="booking-image">
                                    <img 
                                        src={getBookingImage(monthlyHours)} 
                                        alt="Booking level indicator"
                                    />
                                </div>
                                {renderAvatar(employee)}
                                <div className="employee-info">
                                    <h3>{employee.email.split('@')[0]}</h3>
                                    <p className="employee-role">{employee.role || 'Employee'}</p>
                                </div>
                                <div className="employee-stats">
                                    <div className="stat">
                                        <span className="stat-label">Monthly Hours</span>
                                        <span className="stat-value">{monthlyHours}/160h</span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">Tasks</span>
                                        <span className="stat-value">0</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Employees; 